import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  InputAdornment,
  Menu as MuiMenu,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  TextField,
  Typography,
  makeStyles,
  useMediaQuery,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import { hostname } from "../providers/api";
import { formatDate, formatMoney, formatPercent } from "./commercialFormatters";
import {
  localDateTimeInputToIso,
  toLocalDateTimeInputValue,
} from "./commercialDateTime";
import {
  profitabilityCoverageLabel,
  summarizeActions,
} from "./commercialInsightsState";
import { applyOpportunityFilters } from "./commercialTableState";
import {
  getCommercialIntelligenceDataPaths,
  groupCurrentPeriodMovements,
  movementTypeOrder,
} from "./commercialIntelligenceState";
import { CommercialEmpty } from "./CommercialUi";
import CommercialSalesBoard from "./CommercialSalesBoard";
import CommercialPriceCalculator from "./CommercialPriceCalculator";
import { getWorkflowActionState } from "./commercialWorkflowState";
import {
  commercialActionErrorMessage,
  closedOpportunityRows,
  contactRecencyLabel,
  defaultOpportunitySort,
  editableOpportunityPayload,
  archiveOpportunityPayload,
  groupPipelineByStage,
  nextOpportunitySort,
  opportunityPrimaryAction,
  opportunityOutcomeLabel,
  opportunitySecondaryActions,
  opportunityTransitionFollowUp,
  opportunityTransitionImpact,
  lostTransitionPayload,
  pipelineStageValueLabel,
  sortOpportunities,
} from "./commercialSalesState";

const useStyles = makeStyles((theme) => ({
  root: { marginTop: theme.spacing(2), minWidth: 0, overflow: "hidden", width: "100%" },
  body: { minWidth: 0, padding: theme.spacing(2) },
  metric: { height: "100%" },
  label: { color: theme.palette.text.secondary },
  positive: { color: theme.palette.success.dark },
  negative: { color: theme.palette.error.main },
  warning: { background: theme.palette.warning.light, border: `1px solid ${theme.palette.warning.main}` },
  reason: { display: "block", color: theme.palette.text.secondary },
  sectionHeader: {
    alignItems: "flex-start", display: "flex", gap: theme.spacing(2), justifyContent: "space-between",
    marginBottom: theme.spacing(1.5),
    [theme.breakpoints.down("xs")]: { flexDirection: "column", "& > button": { width: "100%" } },
  },
  tableContainer: { maxWidth: "100%", overflowX: "auto" },
  salesTable: { minWidth: 1040, tableLayout: "fixed" },
  salesPriorityCell: { width: 112 },
  salesOpportunityCell: { width: 260 },
  salesStageCell: { width: 112 },
  salesValueCell: { width: 140 },
  salesNextCell: { width: 250 },
  salesToolbar: {
    alignItems: "center", display: "flex", flexWrap: "wrap", gap: theme.spacing(1),
    marginTop: theme.spacing(2), padding: theme.spacing(1.5, 0),
    "& .MuiFormControl-root": { minWidth: 150 },
    [theme.breakpoints.down("xs")]: { alignItems: "stretch", flexDirection: "column", "& .MuiFormControl-root": { width: "100%" } },
  },
  salesSummary: {
    alignItems: "center", borderBottom: `1px solid ${theme.palette.divider}`, borderTop: `1px solid ${theme.palette.divider}`,
    display: "flex", flexWrap: "wrap", gap: theme.spacing(1, 3), marginTop: theme.spacing(1), padding: theme.spacing(1.5, 0),
  },
  salesBrand: { color: "#0b9b55", fontWeight: 700, letterSpacing: "-.02em" },
  pipelineContext: { marginTop: theme.spacing(1.5) },
  actionError: {
    background: theme.palette.error.light, border: `1px solid ${theme.palette.error.main}`,
    marginBottom: theme.spacing(2), padding: theme.spacing(1.5),
  },
  secondaryColumn: { [theme.breakpoints.down("sm")]: { display: "none" } },
  actionCell: { whiteSpace: "nowrap", width: 180 },
  mobileList: { display: "grid", gap: theme.spacing(1.5) },
  mobileOpportunity: { border: `1px solid ${theme.palette.divider}`, borderRadius: theme.shape.borderRadius, padding: theme.spacing(2) },
  mobileOpportunityHeader: { alignItems: "flex-start", display: "flex", gap: theme.spacing(1), justifyContent: "space-between" },
  mobileMeta: { display: "grid", gap: theme.spacing(1), gridTemplateColumns: "1fr 1fr", marginTop: theme.spacing(1.5) },
  mobileActions: { display: "flex", flexWrap: "wrap", gap: theme.spacing(1), marginTop: theme.spacing(1.5) },
  stageBoard: { display: "grid", gap: theme.spacing(1), gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", margin: theme.spacing(2, 0) },
  stageColumn: { border: `1px solid ${theme.palette.divider}`, borderRadius: theme.shape.borderRadius, padding: theme.spacing(1.5) },
  featureList: { display: "flex", flexWrap: "wrap", gap: theme.spacing(.5), marginTop: theme.spacing(.5) },
  movementDrilldown: {
    borderTop: `1px solid ${theme.palette.divider}`,
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
  },
  movementHeader: {
    alignItems: "flex-start",
    display: "flex",
    gap: theme.spacing(2),
    justifyContent: "space-between",
    [theme.breakpoints.down("sm")]: { alignItems: "stretch", flexDirection: "column" },
  },
  movementFilters: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(.75),
  },
  movementGroup: { background: theme.palette.action.hover },
  movementAmount: { whiteSpace: "nowrap" },
  movementLink: { color: theme.palette.primary.main, fontWeight: 500 },
}));
const Metric = ({ label, value, note, tone }) => {
  const c = useStyles();
  return (
    <Card className={c.metric}>
      <CardContent>
        <Typography variant="body2" className={c.label}>
          {label}
        </Typography>
        <Typography
          variant="h5"
          className={
            tone === "positive"
              ? c.positive
              : tone === "negative"
                ? c.negative
                : ""
          }
        >
          {value}
        </Typography>
        {note && (
          <Typography variant="caption" color="textSecondary">
            {note}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};
const supportEmailFromName = (name) => {
  const slug = String(name || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 55) || "client";
  return `support_${slug}@bcome.com`;
};
const movementMoneyFormatter = new window.Intl.NumberFormat("en-IE", {
  style: "currency",
  currency: "EUR",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const formatMovementMoney = value => movementMoneyFormatter.format(Number(value || 0));

function WorkflowRunResult({ result, classes, onClose }) {
  const customerId = result.output?.customer?.id;
  const statusCopy = {
    BLOCKED: ["Onboarding blocked", result.error || "The saved workflow policy prevents this run."],
    FAILED: ["Onboarding needs attention", result.error || "Open Automation history to inspect the failed step, then return to Sales to try again."],
    COMPLETED: ["Customer onboarding completed", "The governed workflow ran and its customer, account, user and message steps are recorded together."],
  };
  const [title, detail] = statusCopy[result.status] || ["Onboarding run recorded", `Current status: ${result.status}`];
  const steps = result.steps || result.output?.steps || [];
  return <><DialogContent><Card className={result.status === "COMPLETED" ? classes.positive : undefined} variant="outlined"><CardContent>
    <Box display="flex" alignItems="center" justifyContent="space-between" gridGap={12}><Typography variant="h6">{title}</Typography><Chip size="small" color={result.status === "FAILED" || result.status === "BLOCKED" ? "secondary" : "primary"} label={result.status} /></Box>
    <Typography variant="body2" color="textSecondary">{detail}</Typography>
    {steps.length > 0 && <Box mt={2}>{steps.map(step => <Typography variant="body2" key={step.id || step.code}>{step.code.replaceAll("_", " ")} · {step.status}</Typography>)}</Box>}
  </CardContent></Card></DialogContent><DialogActions><Button onClick={onClose}>Close</Button><Button color="primary" href="#/commercial-workflow-history">Open workflow history</Button>{customerId && <Button color="primary" variant="contained" href={`#/commercial/${customerId}/show`}>Open customer</Button>}</DialogActions></>;
}

export default function CommercialIntelligencePanel({ token, section }) {
  const sectionTabs = { metrics: 0, sales: 1, profitability: 2 };
  const classes = useStyles();
  const isCompact = useMediaQuery("(max-width:959.95px)");
  const tab = sectionTabs[section];
  const [data, setData] = useState(null);
  const dataContextRef = useRef({ token: null, section: null });
  const [loadError, setLoadError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [revision, setRevision] = useState(0);
  const emptyForm = {
    company: "",
    customerId: "",
    customerLinkLocked: false,
    stage: "PROSPECT",
    arrPotential: "",
    probability: "0.1",
    nextAction: "",
    nextActionAt: "",
    expectedCloseDate: "",
    budgetSent: false,
    contactName: "",
    contactEmail: "",
    contactPhone: "",
    featureIds: [],
    kind: "NEW",
    pricingSource: "BCOME_PRICING_2026",
    pricingReference: "",
    pricingVersion: "2026.1",
    pricingQuotedAt: new Date().toISOString().slice(0, 10),
    pricingCurrency: "EUR",
    pricingLines: "[]",
    pricingDiscounts: "[]",
    pricingSpecialTerms: "",
    capturePricingSnapshot: false,
    salesOwnerId: "",
  };
  const [form, setForm] = useState(null);
  const [pricingOpportunity, setPricingOpportunity] = useState(null);
  const [customerSearch, setCustomerSearch] = useState("");
  const [nextStepForm, setNextStepForm] = useState(null);
  const [interactionForm, setInteractionForm] = useState(null);
  const [transitionForm, setTransitionForm] = useState(null);
  const [archiveForm, setArchiveForm] = useState(null);
  const [hideForm, setHideForm] = useState(null);
  const [intakeForm, setIntakeForm] = useState(null);
  const [intakeResult, setIntakeResult] = useState(null);
  const [actionMenu, setActionMenu] = useState(null);
  const [conversion, setConversion] = useState(null);
  const [saving, setSaving] = useState(false);
  const [salesSearch, setSalesSearch] = useState("");
  const [salesStage, setSalesStage] = useState("ALL");
  const [salesSeverity, setSalesSeverity] = useState("ALL");
  const [salesPage, setSalesPage] = useState(0);
  const [salesRowsPerPage, setSalesRowsPerPage] = useState(10);
  const [salesSort, setSalesSort] = useState(defaultOpportunitySort);
  const [salesView, setSalesView] = useState("BOARD");
  const [closedSearch, setClosedSearch] = useState("");
  const [closedOutcome, setClosedOutcome] = useState("ALL");
  const [closedPage, setClosedPage] = useState(0);
  const [closedRowsPerPage, setClosedRowsPerPage] = useState(10);
  const [movementFilter, setMovementFilter] = useState("ALL");
  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
  useEffect(() => {
    const headers = { Authorization: `Bearer ${token}` };
    const dataPaths = getCommercialIntelligenceDataPaths(section);
    const entries = Object.entries(dataPaths);
    const contextChanged = dataContextRef.current.token !== token
      || dataContextRef.current.section !== section;
    dataContextRef.current = { token, section };
    if (contextChanged) setData(null);
    setLoadError(null);
    let cancelled = false;
    Promise.all(
      entries.map(([, path]) =>
        fetch(`${hostname}/commercial/${path}`, { headers }).then((r) =>
          r.ok
            ? r.json()
            : Promise.reject(new Error(`${path} failed (${r.status})`)),
        ),
      ),
    )
      .then((results) => {
        if (cancelled) return;
        setLoadError(null);
        setData(Object.fromEntries(entries.map(([key], index) => [key, results[index]])));
      })
      .catch(error => {
        if (!cancelled) setLoadError(error);
      });
    return () => { cancelled = true; };
  }, [token, revision, section]);
  if (loadError && !data)
    return (
      <Card className={`${classes.root} ${classes.warning}`}>
        <CardContent>
          <Typography color="error">This Commercial page could not be loaded</Typography>
          <Typography>{loadError.message}</Typography>
        </CardContent>
      </Card>
    );
  if (!data)
    return (
      <Box className={classes.root} p={3} textAlign="center">
        <CircularProgress size={24} />{" "}
        <Typography component="span">
          Loading business intelligence...
        </Typography>
      </Box>
    );
  const { metrics, sales, profitability, onboardingPolicy, featureCatalog, customers } = data;
  const onboardingMode = onboardingPolicy?.configuredMode || onboardingPolicy?.mode || "MANUAL";
  const salesRows = sales?.actionQueue || [];
  const boardRows = sales?.pipelineBoard || salesRows;
  const salesOwners = sales?.salesOwners || [...new Map(salesRows
    .filter(row=>row.salesOwnerId)
    .map(row=>[row.salesOwnerId,{id:row.salesOwnerId,name:row.owner}])).values()];
  const actions = summarizeActions(salesRows);
  const filteredSales = applyOpportunityFilters(salesRows, { search: salesSearch, stage: salesStage, severity: salesSeverity });
  const sortedSales = sortOpportunities(filteredSales, salesSort);
  const visibleSales = sortedSales.slice(salesPage * salesRowsPerPage, salesPage * salesRowsPerPage + salesRowsPerPage);
  const salesStages = [...new Set(salesRows.map(row => row.stage))];
  const closedSalesRows = closedOpportunityRows(sales?.closedHistory || [], { search: closedSearch, outcome: closedOutcome });
  const visibleClosedSales = closedSalesRows.slice(closedPage * closedRowsPerPage, closedPage * closedRowsPerPage + closedRowsPerPage);
  const pipelineGroups = groupPipelineByStage(salesRows, sales?.stages || salesStages.map((code, index) => ({ code, name: code.replaceAll("_", " "), displayOrder: index })));
  const customerOptions = (customers || []).filter(customer => (
    customer.id === form?.customerId
    || !customerSearch.trim()
    || `${customer.name || ""} ${customer.country || ""}`.toLocaleLowerCase().includes(customerSearch.trim().toLocaleLowerCase())
  ));
  const allMovementGroups = metrics
    ? groupCurrentPeriodMovements(metrics.movementDetails, metrics.period)
    : [];
  const movementGroups = metrics
    ? groupCurrentPeriodMovements(metrics.movementDetails, metrics.period, movementFilter)
    : [];
  const movementCount = allMovementGroups.reduce((sum, group) => sum + group.rows.length, 0);
  const movementCounts = Object.fromEntries(allMovementGroups.map(group => [group.type, group.rows.length]));
  const changeSalesSort = field => {
    setSalesSort(current => nextOpportunitySort(current, field));
    setSalesPage(0);
  };
  const editOpportunity = row => {
    setActionError(null);
    setCustomerSearch("");
    setForm({
      ...row,
      customerId: row.customerId || "",
      customerLinkLocked: Boolean(row.customerId),
      arrPotential: row.value,
      nextActionAt: toLocalDateTimeInputValue(row.nextActionAt),
      expectedCloseDate: row.expectedCloseDate ? String(row.expectedCloseDate).slice(0, 10) : "",
      pricingVersion: row.pricingSnapshot?.version || "2026.1",
      pricingQuotedAt: row.pricingSnapshot?.quotedAt || new Date().toISOString().slice(0, 10),
      pricingCurrency: row.pricingSnapshot?.currency || "EUR",
      pricingLines: JSON.stringify(row.pricingSnapshot?.lines || [], null, 2),
      pricingDiscounts: JSON.stringify(row.pricingSnapshot?.discounts || [], null, 2),
      pricingSpecialTerms: row.pricingSnapshot?.specialTerms || "",
      capturePricingSnapshot: Boolean(row.pricingSnapshot),
    });
  };
  const startOnboarding = async row => {
    setActionError(null);
    setSaving(true);
    try {
      const response = await fetch(`${hostname}/commercial/opportunities/${row.id}/conversion-plan`, { headers });
      if (!response.ok) throw new Error(await response.text());
      const plan = await response.json();
       setConversion({ ...plan, opportunityId: row.id, requestId: `onboarding-${row.id}-${Date.now()}`, accountName: plan.accountName, contactName: plan.licenseUsers[0]?.name || row.contactName || "", contactEmail: plan.licenseUsers[0]?.email || "", extraEmails: "", sendOnboardingEmails: false });
    } catch (cause) { setActionError({ action: "Onboarding setup", message: commercialActionErrorMessage(cause) }); } finally { setSaving(false); }
  };
  const prepareClientIntake = row => {
    setActionError(null);
    setIntakeResult(null);
    setIntakeForm({ opportunity: row, recipientEmail: row.contactEmail || "", language: "es", expiresInDays: 7 });
  };
  const openInteraction = row => {
    setActionError(null);
    setInteractionForm({
      opportunityId: row.id,
      company: row.company,
      channel: "EMAIL",
      direction: "OUTBOUND",
      summary: "",
      outcome: "",
      occurredAt: toLocalDateTimeInputValue(new Date()),
      nextAction: row.nextAction || "",
      nextActionAt: toLocalDateTimeInputValue(row.nextActionAt),
    });
  };
  const openNextStep = row => {
    setActionError(null);
    setNextStepForm({
      opportunityId: row.id,
      company: row.company,
      nextAction: row.nextAction || "",
      nextActionAt: toLocalDateTimeInputValue(row.nextActionAt),
    });
  };
  const openTransition = (row, requestedStage = "") => {
    setActionError(null);
    setTransitionForm({ opportunity: row, stage: requestedStage, lossReasonCode: "", lossReasonNotes: "", recontactAt: "" });
  };
  const openArchive = row => {
    setActionError(null);
    setArchiveForm({opportunity:row,reason:""});
  };
  const runOpportunityAction = (row, action) => {
    if (action.type === "EDIT_DETAILS") editOpportunity(row);
    if (action.type === "PLAN_NEXT_STEP") openNextStep(row);
    if (action.type === "LOG_CONTACT") openInteraction(row);
    if (action.type === "MOVE_STAGE") openTransition(row);
    if (action.type === "ONBOARD") startOnboarding(row);
    if (action.type === "ARCHIVE") openArchive(row);
  };
  const openMoreActions = (event, row, primaryType) => {
    setActionMenu({ anchorEl: event.currentTarget, row, primaryType });
  };
  const closeMoreActions = () => setActionMenu(null);
  const runMoreAction = action => {
    const row = actionMenu?.row;
    closeMoreActions();
    if (row) runOpportunityAction(row, action);
  };
  const comparison = (label, row) =>
    row
      ? `${label}: ${row.mrrChange >= 0 ? "+" : ""}${formatMoney(row.mrrChange)} (${row.mrrChangePercentage === null ? "no baseline" : formatPercent(row.mrrChangePercentage)})`
      : `${label}: not enough history`;
  const onboardingAction = conversion
    ? getWorkflowActionState(conversion.workflowPolicy, "Run onboarding now")
    : null;
  return (
    <Card className={classes.root}>
      <div className={classes.body}>
        {tab === 0 && (
          <>
            <Typography variant="h6">
              SaaS performance · {metrics.period}
            </Typography>
            <Typography color="textSecondary" gutterBottom>
              {metrics.isPartial
                ? "Partial period; completed comparisons should be interpreted cautiously."
                : "Completed reporting period."}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Metric
                  label="ARR"
                  value={metrics.scorecard.arr === null || metrics.scorecard.arr === undefined
                    ? "Not available"
                    : formatMoney(metrics.scorecard.arr)}
                  note="Latest MRR × 12"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Metric
                  label="ARPA"
                  value={
                    metrics.scorecard.arpa === null
                      ? "Not available"
                      : formatMoney(metrics.scorecard.arpa)
                  }
                  note="MRR per active customer"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Metric
                  label="Gross retention"
                  value={
                    metrics.scorecard.grr === null
                      ? "Not available"
                      : formatPercent(metrics.scorecard.grr)
                  }
                  tone={metrics.scorecard.grr >= 90 ? "positive" : "negative"}
                  note="Excludes expansion"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Metric
                  label="Revenue churn"
                  value={
                    metrics.scorecard.revenueChurn === null
                      ? "Not available"
                      : formatPercent(metrics.scorecard.revenueChurn)
                  }
                  tone={
                    metrics.scorecard.revenueChurn > 2 ? "negative" : "positive"
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Metric
                  label="Customer churn"
                  value={
                    metrics.scorecard.logoChurn === null
                      ? "Not available"
                      : formatPercent(metrics.scorecard.logoChurn)
                  }
                  tone={
                    metrics.scorecard.logoChurn > 2 ? "negative" : "positive"
                  }
                />
              </Grid>
            </Grid>
            <Box mt={2}>
              <Typography variant="subtitle1">MRR waterfall</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {[
                        "Starting",
                        "New",
                        "Reactivation",
                        "Expansion",
                        "Contraction",
                        "Churn",
                        "Net new",
                        "Ending",
                      ].map((x) => (
                        <TableCell align="right" key={x}>
                          {x}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      {[
                        "startingMrr",
                        "newMrr",
                        "reactivation",
                        "expansion",
                        "contraction",
                        "churn",
                        "netNewMrr",
                        "endingMrr",
                      ].map((key) => (
                        <TableCell
                          align="right"
                          key={key}
                          className={
                            Number(metrics.waterfall[key]) < 0
                              ? classes.negative
                              : ""
                          }
                        >
                          {Number(metrics.waterfall[key]) > 0 &&
                          !["startingMrr", "endingMrr"].includes(key)
                            ? "+"
                            : ""}
                          {formatMoney(metrics.waterfall[key])}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
            <Box className={classes.movementDrilldown}>
              <Box className={classes.movementHeader}>
                <div>
                  <Typography variant="h6">Affected customers</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Customer-level revenue events behind the {metrics.period} waterfall. Values show MRR before and after each change.
                  </Typography>
                </div>
                {movementCount > 0 && <div className={classes.movementFilters} role="group" aria-label="Filter affected customers by movement type">
                  <Button
                    size="small"
                    color="primary"
                    variant={movementFilter === "ALL" ? "contained" : "outlined"}
                    aria-pressed={movementFilter === "ALL"}
                    onClick={() => setMovementFilter("ALL")}
                  >
                    All ({movementCount})
                  </Button>
                  {movementTypeOrder.map(type => <Button
                    size="small"
                    color="primary"
                    key={type}
                    variant={movementFilter === type ? "contained" : "outlined"}
                    aria-pressed={movementFilter === type}
                    onClick={() => setMovementFilter(type)}
                  >
                    {type.charAt(0) + type.slice(1).toLowerCase()} ({movementCounts[type] || 0})
                  </Button>)}
                </div>}
              </Box>
              {movementGroups.length ? <TableContainer className={classes.tableContainer}>
                <Table size="small" aria-label={`${metrics.period} affected customers`}>
                  <TableHead><TableRow>
                    <TableCell>Customer</TableCell>
                    <TableCell>Effective date</TableCell>
                    <TableCell align="right">MRR before {"\u2192"} after</TableCell>
                    <TableCell align="right">Delta</TableCell>
                  </TableRow></TableHead>
                  <TableBody>{movementGroups.map(group => <React.Fragment key={group.type}>
                    <TableRow className={classes.movementGroup}>
                      <TableCell colSpan={3}>
                        <Typography variant="subtitle2">
                          {group.label} {"\u00b7"} {group.rows.length} {group.rows.length === 1 ? "customer" : "customers"}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" className={group.total < 0 ? classes.negative : classes.positive}>
                        {group.total > 0 ? "+" : ""}{formatMovementMoney(group.total)}
                      </TableCell>
                    </TableRow>
                    {group.rows.map(row => <TableRow key={row.id || `${row.customerId}-${row.effectiveDate}-${row.type}`}>
                      <TableCell>
                        {row.customerId ? <Typography
                          component="a"
                          href={`#/commercial/${row.customerId}/show`}
                          className={classes.movementLink}
                          variant="body2"
                        >
                          {row.name || "Customer name not recorded"}
                        </Typography> : <Typography variant="body2">{row.name || "Customer identity not recorded"}</Typography>}
                      </TableCell>
                      <TableCell>{formatDate(row.effectiveDate)}</TableCell>
                      <TableCell align="right" className={classes.movementAmount}>
                        {formatMovementMoney(row.previousMrr)} {"\u2192"} {formatMovementMoney(row.nextMrr)}
                      </TableCell>
                      <TableCell
                        align="right"
                        className={`${classes.movementAmount} ${Number(row.delta) < 0 ? classes.negative : classes.positive}`}
                      >
                        {Number(row.delta) > 0 ? "+" : ""}{formatMovementMoney(row.delta)}
                      </TableCell>
                    </TableRow>)}
                  </React.Fragment>)}</TableBody>
                </Table>
              </TableContainer> : <CommercialEmpty
                title={movementFilter === "ALL"
                  ? `No customer movements in ${metrics.period}`
                  : `No ${movementFilter.toLowerCase()} movements in ${metrics.period}`}
                description={movementFilter === "ALL"
                  ? "No customer-level revenue events were recorded for this period. The waterfall therefore has no affected customers to list."
                  : "Choose another movement type or All to review the customer-level events recorded for this period."}
              />}
            </Box>
            <Box mt={2}>
              <Typography>
                {comparison("Month over month", metrics.comparisons.mom)}
              </Typography>
              <Typography>
                {comparison("Quarter over quarter", metrics.comparisons.qoq)}
              </Typography>
              <Typography>
                {comparison("Year over year", metrics.comparisons.yoy)}
              </Typography>
            </Box>
          </>
        )}
        {tab === 1 && (
          <>
            <Box className={classes.sectionHeader}>
              <div>
                <Typography variant="h4" className={classes.salesBrand}>BCome | SALES PIPELINE</Typography>
                <Typography color="textSecondary" gutterBottom>
                  A visual, traceable workspace for ownership, services, pricing and close dates.
                </Typography>
                <Typography variant="h6" className={classes.pipelineContext}>Pipeline view · All markets</Typography>
              </div>
              <Button
                aria-label="New opportunity"
                color="primary"
                variant="contained"
                onClick={() => { setActionError(null); setCustomerSearch(""); setForm(emptyForm); }}
              >
                + New Deal
              </Button>
            </Box>
            {actionError && <Box className={classes.actionError} role="alert">
              <Box display="flex" alignItems="flex-start" justifyContent="space-between" gridGap={16}>
                <div><Typography variant="subtitle2" color="error">{actionError.action} could not be completed</Typography><Typography variant="body2">{actionError.message}</Typography></div>
                <Button size="small" onClick={() => setActionError(null)}>Dismiss</Button>
              </Box>
            </Box>}
            <Box className={classes.salesSummary} aria-label="Pipeline summary">
              <Typography variant="body2"><strong>{sales.summary.opportunities}</strong> opportunities</Typography>
              <Typography variant="body2"><strong>{formatMoney(sales.summary.weightedPipeline)}</strong> weighted pipeline</Typography>
              <Typography variant="body2" className={actions.missingNextAction ? classes.negative : classes.positive}><strong>{actions.missingNextAction}</strong> missing next action</Typography>
              <Typography variant="body2"><strong>{sales.summary.salesVelocity === null ? "Not enough history" : `${formatMoney(sales.summary.salesVelocity)}/day`}</strong> sales velocity</Typography>
            </Box>
            <Tabs value={salesView} onChange={(_, value) => setSalesView(value)} aria-label="Sales workspace views">
              <Tab value="BOARD" label={`Pipeline board (${boardRows.length})`} />
              <Tab value="QUEUE" label="Action queue" />
              <Tab value="CLOSED" label={`Closed history (${(sales.closedHistory || []).length})`} />
            </Tabs>
            {salesView === "BOARD" ? <CommercialSalesBoard
              rows={boardRows}
              stages={sales?.stages || []}
              owners={salesOwners}
              onMove={openTransition}
              onEdit={editOpportunity}
              onPrice={setPricingOpportunity}
              onArchive={openArchive}
              onHide={setHideForm}
              onOnboard={startOnboarding}
              onIntake={prepareClientIntake}
            /> : salesView === "QUEUE" ? <>
            <div className={classes.salesToolbar}>
              <TextField
                id="sales-opportunity-search" size="small" variant="outlined" label="Search opportunities" value={salesSearch}
                onChange={event => { setSalesSearch(event.target.value); setSalesPage(0); }}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
              />
              <FormControl variant="outlined" size="small"><InputLabel>Stage</InputLabel><Select label="Stage" value={salesStage} onChange={event => { setSalesStage(event.target.value); setSalesPage(0); }}><MenuItem value="ALL">All stages</MenuItem>{salesStages.map(stage => <MenuItem value={stage} key={stage}>{stage.replaceAll("_", " ")}</MenuItem>)}</Select></FormControl>
              <FormControl variant="outlined" size="small"><InputLabel>Priority</InputLabel><Select label="Priority" value={salesSeverity} onChange={event => { setSalesSeverity(event.target.value); setSalesPage(0); }}><MenuItem value="ALL">All priorities</MenuItem>{["CRITICAL", "HIGH", "NORMAL"].map(value => <MenuItem value={value} key={value}>{value}</MenuItem>)}</Select></FormControl>
              <Typography variant="body2" color="textSecondary">{filteredSales.length} of {sales.actionQueue.length} opportunities</Typography>
            </div>
            {isCompact ? <div className={classes.mobileList}>
              {visibleSales.map(row => {
                const primaryAction = opportunityPrimaryAction(row, onboardingMode);
                return <article className={classes.mobileOpportunity} key={row.id}>
                <div className={classes.mobileOpportunityHeader}>
                  <div><Typography variant="subtitle1">{row.company}</Typography><Typography variant="caption" color="textSecondary">{row.owner || "Owner not assigned"}</Typography></div>
                  <Chip size="small" color={row.priority.severity === "CRITICAL" ? "secondary" : "default"} variant={row.priority.severity === "NORMAL" ? "outlined" : "default"} label={`${row.priority.score} · ${row.priority.severity}`} />
                </div>
                <div className={classes.mobileMeta}>
                  <div><Typography variant="caption" color="textSecondary">Stage</Typography><Typography variant="body2">{row.stage}</Typography></div>
                  <div><Typography variant="caption" color="textSecondary">Potential ARR</Typography><Typography variant="body2">{formatMoney(row.value)}</Typography></div>
                  <div><Typography variant="caption" color="textSecondary">Point of contact</Typography><Typography variant="body2" className={row.contactEmail ? undefined : classes.negative}>{row.contactName || row.contactEmail || "Contact missing"}</Typography></div>
                  <div><Typography variant="caption" color="textSecondary">Last contact</Typography><Typography variant="body2" className={row.contactHealth === "STALE" || row.contactHealth === "MISSING" ? classes.negative : undefined}>{contactRecencyLabel(row)}</Typography></div>
                  <div><Typography variant="caption" color="textSecondary">Next action</Typography><Typography variant="body2" className={row.nextAction ? undefined : classes.negative}>{row.nextAction || "Next action missing"}</Typography><Typography variant="caption" color="textSecondary">{row.nextActionAt ? formatDate(row.nextActionAt) : "No date"}</Typography></div>
                </div>
                {row.features?.length > 0 && <div className={classes.featureList}>{row.features.map(feature => <Chip size="small" variant="outlined" key={feature.id} label={feature.name} />)}</div>}
                <div className={classes.mobileActions}>
                  <Button size="small" color="primary" variant="contained" href={primaryAction.type === "OPEN_CUSTOMER" ? `#/commercial/${row.customerId}/show` : undefined} onClick={primaryAction.type === "OPEN_CUSTOMER" ? undefined : () => runOpportunityAction(row, primaryAction)}>{primaryAction.label}</Button>
                  {opportunitySecondaryActions(row, primaryAction.type).length > 0 && <Button size="small" aria-label={`More actions for ${row.company}`} onClick={event => openMoreActions(event, row, primaryAction.type)}>More actions</Button>}
                </div>
              </article>;})}
              {visibleSales.length === 0 && <CommercialEmpty title="No opportunities found" description="Adjust the search or filters to see more of the sales queue." />}
            </div> : <TableContainer className={classes.tableContainer}>
              <Table size="small" className={classes.salesTable} aria-label="Sales opportunities">
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.salesPriorityCell} sortDirection={salesSort.field === "priority" ? salesSort.direction : false}>
                      <TableSortLabel active={salesSort.field === "priority"} direction={salesSort.field === "priority" ? salesSort.direction : "desc"} onClick={() => changeSalesSort("priority")}>Priority</TableSortLabel>
                    </TableCell>
                    <TableCell className={classes.salesOpportunityCell} sortDirection={salesSort.field === "company" ? salesSort.direction : false}>
                      <TableSortLabel active={salesSort.field === "company"} direction={salesSort.field === "company" ? salesSort.direction : "asc"} onClick={() => changeSalesSort("company")}>Opportunity</TableSortLabel>
                    </TableCell>
                    <TableCell className={classes.salesStageCell} sortDirection={salesSort.field === "stage" ? salesSort.direction : false}>
                      <TableSortLabel active={salesSort.field === "stage"} direction={salesSort.field === "stage" ? salesSort.direction : "asc"} onClick={() => changeSalesSort("stage")}>Stage</TableSortLabel>
                    </TableCell>
                    <TableCell className={classes.salesValueCell} align="right" sortDirection={salesSort.field === "potentialArr" ? salesSort.direction : false}>
                      <TableSortLabel active={salesSort.field === "potentialArr"} direction={salesSort.field === "potentialArr" ? salesSort.direction : "desc"} onClick={() => changeSalesSort("potentialArr")}>Value</TableSortLabel>
                    </TableCell>
                    <TableCell className={classes.salesNextCell} sortDirection={salesSort.field === "nextActionAt" ? salesSort.direction : false}>
                      <TableSortLabel active={salesSort.field === "nextActionAt"} direction={salesSort.field === "nextActionAt" ? salesSort.direction : "desc"} onClick={() => changeSalesSort("nextActionAt")}>Next work</TableSortLabel>
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleSales.map((row) => {
                    const primaryAction = opportunityPrimaryAction(row, onboardingMode);
                    return <TableRow hover key={row.id}>
                      <TableCell>
                        <Chip
                          size="small"
                          color={
                            row.priority.severity === "CRITICAL"
                              ? "secondary"
                              : "default"
                          }
                          variant={
                            row.priority.severity === "NORMAL"
                              ? "outlined"
                              : "default"
                          }
                          label={`${row.priority.score} · ${row.priority.severity}`}
                        />
                      </TableCell>
                      <TableCell>
                        {row.company}
                        <Typography
                          variant="caption"
                          className={classes.reason}
                        >
                          {row.owner || "Owner not assigned"}
                        </Typography>
                        <Typography variant="caption" className={row.contactEmail ? classes.reason : classes.negative}>
                          {row.contactName || "Contact missing"}{row.contactEmail ? ` · ${row.contactEmail}` : " · No email"}
                        </Typography>
                        {row.features?.length > 0 && <div className={classes.featureList}>{row.features.map(feature => <Chip size="small" variant="outlined" key={feature.id} label={feature.name} />)}</div>}
                      </TableCell>
                      <TableCell>{row.stage}</TableCell>
                      <TableCell align="right">
                        {formatMoney(row.value)}
                        <Typography variant="caption" className={classes.reason}>Weighted {formatMoney(
                          Number(row.value) * Number(row.probability),
                        )}</Typography>
                      </TableCell>
                      <TableCell>
                        {row.nextAction || (
                          <span className={classes.negative}>
                            Add next action
                          </span>
                        )}
                        <Typography variant="caption" className={classes.reason}>{row.nextActionAt ? formatDate(row.nextActionAt) : "No date"}</Typography>
                        <span className={row.contactHealth === "STALE" || row.contactHealth === "MISSING" ? classes.negative : classes.reason}>{contactRecencyLabel(row)}</span>
                        {row.priority.reasons.map((reason) => (
                          <span className={classes.reason} key={reason}>
                            {reason}
                          </span>
                        ))}
                      </TableCell>
                      <TableCell className={classes.actionCell}>
                        <Button size="small" color="primary" variant="contained" href={primaryAction.type === "OPEN_CUSTOMER" ? `#/commercial/${row.customerId}/show` : undefined} onClick={primaryAction.type === "OPEN_CUSTOMER" ? undefined : () => runOpportunityAction(row, primaryAction)}>{primaryAction.label}</Button>
                        {opportunitySecondaryActions(row, primaryAction.type).length > 0 && <Button size="small" aria-label={`More actions for ${row.company}`} onClick={event => openMoreActions(event, row, primaryAction.type)}>More</Button>}
                      </TableCell>
                    </TableRow>
                  })}
                  {visibleSales.length === 0 && <TableRow><TableCell colSpan={6}><CommercialEmpty title="No opportunities found" description="Adjust the search or filters to see more of the sales queue." /></TableCell></TableRow>}
                </TableBody>
              </Table>
            </TableContainer>}
            <TablePagination
              component="div" count={filteredSales.length} page={salesPage} rowsPerPage={salesRowsPerPage}
              onPageChange={(_, nextPage) => setSalesPage(nextPage)}
              onRowsPerPageChange={event => { setSalesRowsPerPage(Number(event.target.value)); setSalesPage(0); }}
              rowsPerPageOptions={[10, 25, 50]}
            />
            <Box mt={2}>
              <Typography variant="subtitle1">Pipeline by stage</Typography>
              <div className={classes.stageBoard} aria-label="Pipeline by stage">
                {pipelineGroups.map(group => <div className={classes.stageColumn} key={group.code}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle2">{group.name || group.code.replaceAll("_", " ")}</Typography>
                    <Chip size="small" label={group.rows.length} />
                  </Box>
                  <Typography variant="body2">{formatMoney(group.rows.reduce((sum, row) => sum + Number(row.value || 0), 0))}</Typography>
                  <Typography variant="caption" color="textSecondary">{pipelineStageValueLabel(group)}</Typography>
                </div>)}
              </div>
            </Box>
            </> : <>
              <div className={classes.salesToolbar}>
                <TextField
                  id="closed-opportunity-search" size="small" variant="outlined" label="Search closed deals" value={closedSearch}
                  onChange={event => { setClosedSearch(event.target.value); setClosedPage(0); }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                />
                <FormControl variant="outlined" size="small"><InputLabel>Outcome</InputLabel><Select label="Outcome" value={closedOutcome} onChange={event => { setClosedOutcome(event.target.value); setClosedPage(0); }}><MenuItem value="ALL">Won, lost and archived</MenuItem><MenuItem value="WON">Won</MenuItem><MenuItem value="LOST">Lost</MenuItem><MenuItem value="ARCHIVED">Archived</MenuItem></Select></FormControl>
                <Typography variant="body2" color="textSecondary">{closedSalesRows.length} of {(sales.closedHistory || []).length} closed deals</Typography>
              </div>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Historical outcomes are read-only here. They never contribute to active-pipeline totals.
              </Typography>
              {isCompact ? <div className={classes.mobileList} aria-label="Closed opportunities">
                {visibleClosedSales.map(row => <article className={classes.mobileOpportunity} key={row.id}>
                  <div className={classes.mobileOpportunityHeader}>
                    <div><Typography variant="subtitle1">{row.company}</Typography><Typography variant="caption" color="textSecondary">{row.owner || "Owner not assigned"}</Typography></div>
                    <Chip size="small" color={opportunityOutcomeLabel(row) === "WON" ? "primary" : "default"} variant="outlined" label={opportunityOutcomeLabel(row)} />
                  </div>
                  <div className={classes.mobileMeta}>
                    <div><Typography variant="caption" color="textSecondary">Closed</Typography><Typography variant="body2">{formatDate(row.archivedAt||row.closedAt)}</Typography></div>
                    <div><Typography variant="caption" color="textSecondary">Value</Typography><Typography variant="body2">{formatMoney(row.value)}</Typography></div>
                  </div>
                  <Typography variant="body2" color="textSecondary">{row.archiveReason||row.lossReason || (opportunityOutcomeLabel(row) === "WON" ? "Customer handoff recorded" : "No outcome detail recorded")}</Typography>
                  <Box mt={1} display="flex" gridGap={8} flexWrap="wrap">
                    {opportunityOutcomeLabel(row) === "WON" && <Button size="small" color="primary" onClick={() => prepareClientIntake(row)}>Prepare intake</Button>}
                    {row.customerId && <Button size="small" href={`#/commercial/${row.customerId}/show`}>Open customer</Button>}
                  </Box>
                </article>)}
                {!visibleClosedSales.length && <CommercialEmpty title="No closed deals found" description="Adjust the search or outcome filter." />}
              </div> : <TableContainer className={classes.tableContainer}>
                <Table size="small" className={classes.salesTable} aria-label="Closed opportunities">
                  <TableHead><TableRow><TableCell>Outcome</TableCell><TableCell>Opportunity</TableCell><TableCell>Closed</TableCell><TableCell align="right">Value</TableCell><TableCell>Owner</TableCell><TableCell>Outcome detail</TableCell><TableCell>Customer</TableCell></TableRow></TableHead>
                  <TableBody>{visibleClosedSales.map(row => <TableRow key={row.id}>
                    <TableCell><Chip size="small" color={opportunityOutcomeLabel(row) === "WON" ? "primary" : "default"} variant="outlined" label={opportunityOutcomeLabel(row)} /></TableCell>
                    <TableCell>{row.company}<Typography variant="caption" className={classes.reason}>{row.contactName || row.contactEmail || "No contact recorded"}</Typography></TableCell>
                    <TableCell>{formatDate(row.archivedAt||row.closedAt)}</TableCell>
                    <TableCell align="right">{formatMoney(row.value)}</TableCell>
                    <TableCell>{row.owner || "Not assigned"}</TableCell>
                    <TableCell>{row.archiveReason||row.lossReason || (opportunityOutcomeLabel(row) === "WON" ? "Customer handoff recorded" : "No outcome detail recorded")}</TableCell>
                    <TableCell>{opportunityOutcomeLabel(row) === "WON" ? <Box display="flex" gridGap={4}><Button size="small" color="primary" onClick={() => prepareClientIntake(row)}>Prepare intake</Button>{row.customerId && <Button size="small" href={`#/commercial/${row.customerId}/show`}>Customer</Button>}</Box> : "-"}</TableCell>
                  </TableRow>)}
                  {!visibleClosedSales.length && <TableRow><TableCell colSpan={7}><CommercialEmpty title="No closed deals found" description="Adjust the search or outcome filter." /></TableCell></TableRow>}
                  </TableBody>
                </Table>
              </TableContainer>}
              <TablePagination
                component="div" count={closedSalesRows.length} page={closedPage} rowsPerPage={closedRowsPerPage}
                onPageChange={(_, nextPage) => setClosedPage(nextPage)}
                onRowsPerPageChange={event => { setClosedRowsPerPage(Number(event.target.value)); setClosedPage(0); }}
                rowsPerPageOptions={[10, 25, 50]}
              />
            </>}
          </>
        )}
        {tab === 2 && (
          <>
            <Typography variant="h6">
              Profitability and unit economics
            </Typography>
            <Typography color="textSecondary">
              Revenue metrics are live. Margin metrics require explicit cost
              data.
            </Typography>
            <Grid container spacing={2} style={{ marginTop: 4 }}>
              <Grid item xs={12} md={4}>
                <Metric
                  label="Cost-data coverage"
                  value={`${Number(profitability.coverage.percentage).toFixed(0)}%`}
                  note={profitabilityCoverageLabel(profitability.coverage)}
                  tone={
                    profitability.coverage.percentage === 100
                      ? "positive"
                      : "negative"
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Metric
                  label="Gross margin"
                  value={
                    profitability.economics
                      ? formatPercent(profitability.economics.grossMargin)
                      : "Cost data required"
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Metric
                  label="Contribution profit"
                  value={
                    profitability.economics
                      ? formatMoney(profitability.economics.contributionProfit)
                      : "Cost data required"
                  }
                />
              </Grid>
            </Grid>
            {profitability.message && (
              <Card className={classes.warning} style={{ marginTop: 16 }}>
                <CardContent>
                  <Typography variant="subtitle1">What to add next</Typography>
                  <Typography>{profitability.message}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Required: direct delivery/infrastructure cost, support cost,
                    Sales cost, acquisition spend and new customers by channel.
                  </Typography>
                  <Box mt={2} display="flex" flexWrap="wrap" gridGap={8}>
                    <Button color="primary" variant="outlined" href="#/commercial-entry?type=cost">Add customer costs</Button>
                    <Button color="primary" href="#/commercial-entry?type=acquisition">Add acquisition spend</Button>
                  </Box>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
      {actionMenu && <MuiMenu
        anchorEl={actionMenu.anchorEl}
        keepMounted
        open={Boolean(actionMenu.anchorEl)}
        onClose={closeMoreActions}
        aria-label={`More actions for ${actionMenu.row.company}`}
      >
        {opportunitySecondaryActions(actionMenu.row, actionMenu.primaryType).map(action => (
          action.type === "OPEN_CUSTOMER"
            ? <MenuItem component="a" href={`#/commercial/${actionMenu.row.customerId}/show`} key={action.type} onClick={closeMoreActions}>{action.label}</MenuItem>
            : <MenuItem key={action.type} onClick={() => runMoreAction(action)}>{action.label}</MenuItem>
        ))}
      </MuiMenu>}
      {intakeForm && <Dialog open fullWidth maxWidth="sm" onClose={() => !saving && setIntakeForm(null)} aria-labelledby="client-intake-title">
        <DialogTitle id="client-intake-title">Prepare client intake · {intakeForm.opportunity.company}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            This creates a revocable, expiring link. It does not send an email; copy the link only after reviewing the recipient and language.
          </Typography>
          {intakeResult ? <Box mt={2} display="grid" gridGap={12}>
            <Typography variant="subtitle2">Intake link ready</Typography>
            <TextField variant="outlined" label="Secure intake link" value={intakeResult.intakeUrl || ""} InputProps={{ readOnly: true }} />
            <Typography variant="caption" color="textSecondary">The raw token is shown only in this response. Revoke and issue a new intake if it is exposed.</Typography>
          </Box> : <Box pt={1} display="grid" gridGap={16}>
            <TextField variant="outlined" type="email" required label="Recipient email" value={intakeForm.recipientEmail} onChange={event => setIntakeForm({...intakeForm,recipientEmail:event.target.value})} />
            <FormControl variant="outlined"><InputLabel>Language</InputLabel><Select label="Language" value={intakeForm.language} onChange={event => setIntakeForm({...intakeForm,language:event.target.value})}><MenuItem value="es">Spanish</MenuItem><MenuItem value="en">English</MenuItem><MenuItem value="fr">French</MenuItem></Select></FormControl>
            <TextField variant="outlined" type="number" label="Expires in days" inputProps={{min:1,max:30}} value={intakeForm.expiresInDays} onChange={event => setIntakeForm({...intakeForm,expiresInDays:Number(event.target.value)})} />
          </Box>}
        </DialogContent>
        <DialogActions>
          <Button disabled={saving} onClick={() => setIntakeForm(null)}>{intakeResult ? "Close" : "Cancel"}</Button>
          {!intakeResult && <Button disabled={saving || !intakeForm.recipientEmail.trim()} color="primary" variant="contained" onClick={async()=>{
            setSaving(true); setActionError(null);
            try {
              const response=await fetch(`${hostname}/commercial/opportunities/${intakeForm.opportunity.id}/client-intake`,{method:"POST",headers,body:JSON.stringify({recipientEmail:intakeForm.recipientEmail,language:intakeForm.language,expiresInDays:intakeForm.expiresInDays})});
              if(!response.ok) throw new Error(await response.text());
              setIntakeResult(await response.json());
            } catch(cause){setActionError({action:"Client intake",message:commercialActionErrorMessage(cause)});} finally {setSaving(false);}
          }}>Create secure link</Button>}
        </DialogActions>
      </Dialog>}
      {pricingOpportunity && <CommercialPriceCalculator token={token} opportunity={pricingOpportunity} onClose={()=>setPricingOpportunity(null)} onSaved={()=>{setPricingOpportunity(null);setRevision(value=>value+1);}}/>}
      {form && (
        <Dialog
          open
          onClose={() => !saving && setForm(null)}
          fullWidth
          maxWidth="sm"
          aria-labelledby="opportunity-editor-title"
        >
          <DialogTitle id="opportunity-editor-title">
            {form.id ? `Edit opportunity details · ${form.company}` : "New opportunity"}
          </DialogTitle>
          <DialogContent>
            {form.id && <Typography variant="body2" color="textSecondary" gutterBottom>Update deal and contact information here. To change its pipeline stage, use the separate Move stage action.</Typography>}
            {actionError && <Box className={classes.actionError} role="alert"><Typography variant="subtitle2" color="error">{actionError.action} could not be completed</Typography><Typography variant="body2">{actionError.message}</Typography></Box>}
            <Box display="grid" gridGap={16} pt={1}>
              <TextField
                variant="outlined"
                label="Company"
                required
                inputProps={{ "aria-label": "Company" }}
                value={form.company}
                onChange={(e) => setForm({ ...form, company: e.target.value })}
              />
              <FormControl variant="outlined">
                <InputLabel>Opportunity type</InputLabel>
                <Select label="Opportunity type" value={form.kind || "NEW"} onChange={event => setForm({...form,kind:event.target.value})}>
                  <MenuItem value="NEW">New client</MenuItem>
                  <MenuItem value="RENEWAL">Renewal</MenuItem>
                  <MenuItem value="EXPANSION">Expansion</MenuItem>
                </Select>
              </FormControl>
              <FormControl variant="outlined">
                <InputLabel>Sales owner</InputLabel>
                <Select label="Sales owner" value={form.salesOwnerId || ""} onChange={event => setForm({...form,salesOwnerId:event.target.value})}>
                  <MenuItem value=""><em>Owner not assigned</em></MenuItem>
                  {salesOwners.map(owner => <MenuItem key={owner.id} value={owner.id}>{owner.name || owner.email}</MenuItem>)}
                </Select>
              </FormControl>
              <Typography variant="subtitle2">Existing customer link</Typography>
              <Typography variant="body2" color="textSecondary">
                Optional for expansions or new deals with an existing customer. If this opportunity reaches a won outcome, onboarding reuses that customer instead of creating a duplicate. The link cannot be changed after it is saved.
              </Typography>
              <TextField
                variant="outlined"
                label="Find existing customer"
                inputProps={{ "aria-label": "Find existing customer" }}
                value={customerSearch}
                disabled={form.customerLinkLocked}
                onChange={event => setCustomerSearch(event.target.value)}
              />
              <FormControl variant="outlined" disabled={form.customerLinkLocked}>
                <InputLabel id="opportunity-customer-label">Linked customer (optional)</InputLabel>
                <Select
                  labelId="opportunity-customer-label"
                  id="opportunity-customer"
                  label="Linked customer (optional)"
                  value={form.customerId || ""}
                  onChange={event => setForm({ ...form, customerId: event.target.value })}
                >
                  <MenuItem value=""><em>No linked customer</em></MenuItem>
                  {customerOptions.map(customer => <MenuItem key={customer.id} value={customer.id}>
                    {customer.name}{customer.country ? ` · ${customer.country}` : ""}
                  </MenuItem>)}
                </Select>
              </FormControl>
              {form.customerLinkLocked && <Typography variant="caption" color="textSecondary">
                This opportunity is already linked to a customer; the saved link is read-only.
              </Typography>}
              <Typography variant="subtitle2">Point of contact</Typography>
              <TextField variant="outlined" label="Contact name" value={form.contactName || ""} onChange={(e) => setForm({ ...form, contactName: e.target.value })} />
              <TextField variant="outlined" type="email" label="Contact email" helperText="Used for onboarding and renewal emails" value={form.contactEmail || ""} onChange={(e) => setForm({ ...form, contactEmail: e.target.value })} />
              <TextField variant="outlined" label="Contact phone" value={form.contactPhone || ""} onChange={(e) => setForm({ ...form, contactPhone: e.target.value })} />
              <FormControl variant="outlined">
                <InputLabel>Features</InputLabel>
                <Select multiple label="Features" value={form.featureIds || []} onChange={(e) => setForm({ ...form, featureIds: e.target.value })} renderValue={selected => selected.map(id => featureCatalog.find(feature => feature.id === id)?.name || id).join(", ")}>
                  {featureCatalog.map(feature => <MenuItem key={feature.id} value={feature.id}>{feature.name}</MenuItem>)}
                </Select>
              </FormControl>
              <Typography variant="caption" color="textSecondary">Selected features are activated automatically on the platform account when onboarding completes.</Typography>
              <Typography variant="subtitle2">Pricing</Typography>
              <Typography variant="body2" color="textSecondary">{form.pricingReference ? `Final quote ${form.pricingReference}` : form.id ? 'Use Price on the pipeline card to calculate and save a governed quote.' : 'Create the opportunity first, then calculate its quote from the pipeline card.'}</Typography>
              <FormControl variant="outlined">
                <InputLabel>Budget sent</InputLabel>
                <Select label="Budget sent" value={form.budgetSent ? "YES" : "NO"} onChange={event => setForm({...form,budgetSent:event.target.value === "YES"})}>
                  <MenuItem value="NO">Not sent</MenuItem><MenuItem value="YES">Sent</MenuItem>
                </Select>
              </FormControl>
              <TextField
                variant="outlined"
                type="number"
                label="Potential ARR"
                value={form.arrPotential}
                onChange={(e) =>
                  setForm({ ...form, arrPotential: e.target.value })
                }
              />
              <TextField
                variant="outlined"
                type="number"
                inputProps={{ min: 0, max: 1, step: 0.05 }}
                label="Probability (0-1)"
                value={form.probability}
                onChange={(e) =>
                  setForm({ ...form, probability: e.target.value })
                }
              />
              <TextField
                variant="outlined"
                label="Next action"
                value={form.nextAction || ""}
                onChange={(e) =>
                  setForm({ ...form, nextAction: e.target.value })
                }
              />
              <TextField
                variant="outlined"
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                label="Next action date"
                value={form.nextActionAt || ""}
                onChange={(e) =>
                  setForm({ ...form, nextActionAt: e.target.value })
                }
              />
              <TextField
                variant="outlined"
                type="date"
                InputLabelProps={{ shrink: true }}
                label="Expected close"
                value={form.expectedCloseDate || ""}
                onChange={(e) =>
                  setForm({ ...form, expectedCloseDate: e.target.value })
                }
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button disabled={saving} onClick={() => setForm(null)}>
              Cancel
            </Button>
            <Button
              disabled={saving || !form.company.trim()}
              color="primary"
              variant="contained"
              onClick={async () => {
                setActionError(null);
                setSaving(true);
                try {
                  const payload = editableOpportunityPayload({
                    ...form,
                    nextActionAt: localDateTimeInputToIso(form.nextActionAt),
                  });
                  if (form.id) {
                    const response = await fetch(
                      `${hostname}/commercial/opportunities/${form.id}`,
                      {
                        method: "PATCH",
                        headers,
                        body: JSON.stringify(payload),
                      },
                    );
                    if (!response.ok) throw new Error(await response.text());
                  } else {
                    const response = await fetch(
                      `${hostname}/commercial/opportunities`,
                      {
                        method: "POST",
                        headers,
                        body: JSON.stringify(payload),
                      },
                    );
                    if (!response.ok) throw new Error(await response.text());
                  }
                  setForm(null);
                  setRevision((value) => value + 1);
                } catch (cause) {
                  setActionError({ action: form.id ? "Opportunity details" : "New opportunity", message: commercialActionErrorMessage(cause) });
                } finally {
                  setSaving(false);
                }
              }}
            >
              {form.id ? "Save details" : "Create opportunity"}
            </Button>
          </DialogActions>
        </Dialog>
      )}
      {nextStepForm && <Dialog open fullWidth maxWidth="sm" onClose={() => !saving && setNextStepForm(null)} aria-labelledby="plan-next-step-title">
        <DialogTitle id="plan-next-step-title">Plan next step for {nextStepForm.company}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary">
            Schedule the work the team should do next. This does not record client contact or change the last-contact date.
          </Typography>
          {actionError && <Box className={classes.actionError} mt={2} role="alert"><Typography variant="subtitle2" color="error">{actionError.action} could not be completed</Typography><Typography variant="body2">{actionError.message}</Typography></Box>}
          <Box display="grid" gridGap={16} pt={2}>
            <TextField
              autoFocus required multiline minRows={2} variant="outlined" label="Next action"
              inputProps={{ "aria-label": "Next action" }}
              value={nextStepForm.nextAction}
              onChange={event => setNextStepForm({ ...nextStepForm, nextAction: event.target.value })}
            />
            <TextField
              required type="datetime-local" variant="outlined" label="Due date and time"
              InputLabelProps={{ shrink: true }} inputProps={{ "aria-label": "Due date and time" }}
              value={nextStepForm.nextActionAt}
              onChange={event => setNextStepForm({ ...nextStepForm, nextActionAt: event.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button disabled={saving} onClick={() => setNextStepForm(null)}>Cancel</Button>
          <Button disabled={saving || !nextStepForm.nextAction.trim() || !nextStepForm.nextActionAt} color="primary" variant="contained" onClick={async () => {
            setActionError(null);
            setSaving(true);
            try {
              const response = await fetch(`${hostname}/commercial/opportunities/${nextStepForm.opportunityId}`, {
                method: "PATCH",
                headers,
                body: JSON.stringify({
                  nextAction: nextStepForm.nextAction.trim(),
                  nextActionAt: localDateTimeInputToIso(nextStepForm.nextActionAt),
                }),
              });
              if (!response.ok) throw new Error(await response.text());
              setNextStepForm(null);
              setRevision(value => value + 1);
            } catch (cause) {
              setActionError({ action: "Next step", message: commercialActionErrorMessage(cause) });
            } finally { setSaving(false); }
          }}>{saving ? "Saving..." : "Save next step"}</Button>
        </DialogActions>
      </Dialog>}
      {transitionForm && (() => {
        const selectedStage = (sales.stages || []).find(stage => stage.code === transitionForm.stage);
        const impact = selectedStage
          ? opportunityTransitionImpact(selectedStage, onboardingMode)
          : null;
        return <Dialog open fullWidth maxWidth="sm" onClose={() => !saving && setTransitionForm(null)} aria-labelledby="opportunity-stage-title">
          <DialogTitle id="opportunity-stage-title">Move stage · {transitionForm.opportunity.company}</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Current stage: <strong>{transitionForm.opportunity.stage.replaceAll("_", " ")}</strong>. Choose the destination and review its effects before confirming.
            </Typography>
            {actionError && <Box className={classes.actionError} role="alert"><Typography variant="subtitle2" color="error">{actionError.action} could not be completed</Typography><Typography variant="body2">{actionError.message}</Typography></Box>}
            <Box display="grid" gridGap={16} pt={2}>
              <FormControl variant="outlined">
                <InputLabel id="opportunity-new-stage-label">New stage</InputLabel>
                <Select labelId="opportunity-new-stage-label" id="opportunity-new-stage" label="New stage" value={transitionForm.stage} onChange={event => setTransitionForm({ ...transitionForm, stage: event.target.value, lossReasonCode: "", lossReasonNotes: "", recontactAt: "" })}>
                  {(sales.stages || []).filter(stage => stage.code !== transitionForm.opportunity.stage).map(stage => <MenuItem key={stage.code} value={stage.code}>{stage.name}</MenuItem>)}
                </Select>
              </FormControl>
              {impact && <Card variant="outlined"><CardContent>
                <Typography variant="subtitle1">{impact.title}</Typography>
                <Typography variant="body2" color="textSecondary">{impact.detail}</Typography>
              </CardContent></Card>}
              {impact?.requiresLossReason && <>
                <FormControl variant="outlined" required><InputLabel id="opportunity-loss-reason-label">Loss reason</InputLabel>
                  <Select autoFocus labelId="opportunity-loss-reason-label" id="opportunity-loss-reason" label="Loss reason" value={transitionForm.lossReasonCode} onChange={event => setTransitionForm({...transitionForm,lossReasonCode:event.target.value})}>
                    <MenuItem value="COMPETITION">Competition</MenuItem><MenuItem value="BUDGET">Budget</MenuItem>
                    <MenuItem value="TIMING">Timing</MenuItem><MenuItem value="NO_RESPONSE">No response</MenuItem>
                    <MenuItem value="PRODUCT_FIT">Product fit</MenuItem><MenuItem value="TECHNICAL_FIT">Technical fit</MenuItem>
                    <MenuItem value="INTERNAL_PRIORITY">Internal priority</MenuItem><MenuItem value="OTHER">Other</MenuItem>
                  </Select>
                </FormControl>
                <TextField variant="outlined" multiline minRows={3} inputProps={{"aria-label":"Loss notes"}} required={transitionForm.lossReasonCode === "OTHER"} label="Loss notes" value={transitionForm.lossReasonNotes} onChange={event => setTransitionForm({...transitionForm,lossReasonNotes:event.target.value})} helperText={transitionForm.lossReasonCode === "OTHER" ? "Required for Other." : "Optional context for reporting and the next commercial attempt."}/>
                <TextField variant="outlined" type="date" inputProps={{"aria-label":"Re-contact date (optional)"}} InputLabelProps={{shrink:true}} label="Re-contact date (optional)" value={transitionForm.recontactAt} onChange={event => setTransitionForm({...transitionForm,recontactAt:event.target.value})} helperText="On this date the system creates a new linked opportunity; it never reopens this Lost record."/>
              </>}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button disabled={saving} onClick={() => setTransitionForm(null)}>Cancel</Button>
            <Button disabled={saving || !transitionForm.stage || (impact?.requiresLossReason && (!transitionForm.lossReasonCode || (transitionForm.lossReasonCode === "OTHER" && !transitionForm.lossReasonNotes.trim())))} color="primary" variant="contained" onClick={async () => {
              setActionError(null);
              setSaving(true);
              try {
                const response = await fetch(`${hostname}/commercial/opportunities/${transitionForm.opportunity.id}/transition`, {
                  method: "POST",
                  headers,
                  body: JSON.stringify(impact?.requiresLossReason
                    ? lostTransitionPayload(transitionForm.stage,transitionForm)
                    : {stage:transitionForm.stage}),
                });
                if (!response.ok) throw new Error(await response.text());
                const result = await response.json();
                const transitionAction = opportunityTransitionFollowUp(selectedStage, result);
                const opportunity = transitionForm.opportunity;
                setTransitionForm(null);
                if (transitionAction.type === "OPEN_SETUP") {
                  await startOnboarding(opportunity);
                } else if (transitionAction.type === "SHOW_RESULT") {
                  setConversion({ opportunity: { id: opportunity.id, company: opportunity.company }, opportunityId: opportunity.id, result: transitionAction.run });
                }
                setRevision(value => value + 1);
              } catch (cause) {
                setActionError({ action: "Stage change", message: commercialActionErrorMessage(cause) });
              } finally {
                setSaving(false);
              }
            }}>{saving ? "Moving…" : "Confirm stage change"}</Button>
          </DialogActions>
        </Dialog>;
      })()}
      {archiveForm && <Dialog open fullWidth maxWidth="sm" onClose={() => !saving && setArchiveForm(null)} aria-labelledby="archive-opportunity-title">
        <DialogTitle id="archive-opportunity-title">Archive opportunity · {archiveForm.opportunity.company}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary">Archive removes this record from the active pipeline without deleting it. Its reason and actor remain in closed history and audit records.</Typography>
          {actionError && <Box className={classes.actionError} mt={2} role="alert"><Typography variant="subtitle2" color="error">{actionError.action} could not be completed</Typography><Typography variant="body2">{actionError.message}</Typography></Box>}
          <Box pt={2}><TextField autoFocus fullWidth required multiline minRows={3} variant="outlined" inputProps={{"aria-label":"Archive reason"}} label="Archive reason" value={archiveForm.reason} onChange={event=>setArchiveForm({...archiveForm,reason:event.target.value})} helperText="For example: duplicate import, test record or created by mistake."/></Box>
        </DialogContent>
        <DialogActions>
          <Button disabled={saving} onClick={()=>setArchiveForm(null)}>Cancel</Button>
          <Button disabled={saving||!archiveForm.reason.trim()} color="secondary" variant="contained" onClick={async()=>{
            setActionError(null);setSaving(true);
            try{
              const response=await fetch(`${hostname}/commercial/opportunities/${archiveForm.opportunity.id}/archive`,{method:"POST",headers,body:JSON.stringify(archiveOpportunityPayload(archiveForm.reason))});
              if(!response.ok)throw new Error(await response.text());
              setArchiveForm(null);setRevision(value=>value+1);
            }catch(cause){setActionError({action:"Archive opportunity",message:commercialActionErrorMessage(cause)});}
            finally{setSaving(false);}
          }}>{saving?"Archiving…":"Archive opportunity"}</Button>
        </DialogActions>
      </Dialog>}
      {hideForm && <Dialog open fullWidth maxWidth="sm" onClose={() => !saving && setHideForm(null)} aria-labelledby="hide-opportunity-title">
        <DialogTitle id="hide-opportunity-title">Hide lost opportunity · {hideForm.company}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary">This removes the lost opportunity from the pipeline board. Its lost outcome, reason, re-contact date and audit history remain available in Closed history.</Typography>
          {actionError && <Box className={classes.actionError} mt={2} role="alert"><Typography variant="subtitle2" color="error">{actionError.action} could not be completed</Typography><Typography variant="body2">{actionError.message}</Typography></Box>}
        </DialogContent>
        <DialogActions>
          <Button disabled={saving} onClick={()=>setHideForm(null)}>Cancel</Button>
          <Button disabled={saving} color="secondary" variant="contained" onClick={async()=>{
            setActionError(null);setSaving(true);
            try{
              const response=await fetch(`${hostname}/commercial/opportunities/${hideForm.id}/hide`,{method:"POST",headers});
              if(!response.ok)throw new Error(await response.text());
              setHideForm(null);setRevision(value=>value+1);
            }catch(cause){setActionError({action:"Hide opportunity",message:commercialActionErrorMessage(cause)});}
            finally{setSaving(false);}
          }}>{saving?"Hiding…":"Hide opportunity"}</Button>
        </DialogActions>
      </Dialog>}
      {interactionForm && <Dialog open fullWidth maxWidth="sm" onClose={() => !saving && setInteractionForm(null)}>
        <DialogTitle>Log client contact · {interactionForm.company}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary">Record an actual client interaction. Editing an opportunity does not count as contact.</Typography>
          {actionError && <Box className={classes.actionError} role="alert"><Typography variant="subtitle2" color="error">{actionError.action} could not be completed</Typography><Typography variant="body2">{actionError.message}</Typography></Box>}
          <Box display="grid" gridGap={16} pt={2}>
            <FormControl variant="outlined"><InputLabel>Channel</InputLabel><Select label="Channel" value={interactionForm.channel} onChange={event => setInteractionForm({ ...interactionForm, channel: event.target.value })}>{["EMAIL","CALL","MEETING","MESSAGE","OTHER"].map(value => <MenuItem value={value} key={value}>{value}</MenuItem>)}</Select></FormControl>
            <FormControl variant="outlined"><InputLabel>Direction</InputLabel><Select label="Direction" value={interactionForm.direction} onChange={event => setInteractionForm({ ...interactionForm, direction: event.target.value })}><MenuItem value="OUTBOUND">Outbound</MenuItem><MenuItem value="INBOUND">Inbound</MenuItem></Select></FormControl>
            <TextField required multiline minRows={3} variant="outlined" label="What happened?" value={interactionForm.summary} onChange={event => setInteractionForm({ ...interactionForm, summary: event.target.value })} />
            <TextField variant="outlined" label="Outcome" value={interactionForm.outcome} onChange={event => setInteractionForm({ ...interactionForm, outcome: event.target.value })} />
            <TextField type="datetime-local" InputLabelProps={{ shrink: true }} variant="outlined" label="Contact date" value={interactionForm.occurredAt} onChange={event => setInteractionForm({ ...interactionForm, occurredAt: event.target.value })} />
            <TextField variant="outlined" label="Next action" value={interactionForm.nextAction} onChange={event => setInteractionForm({ ...interactionForm, nextAction: event.target.value })} />
            <TextField type="datetime-local" InputLabelProps={{ shrink: true }} variant="outlined" label="Next action date" value={interactionForm.nextActionAt} onChange={event => setInteractionForm({ ...interactionForm, nextActionAt: event.target.value })} />
          </Box>
        </DialogContent>
        <DialogActions><Button onClick={() => setInteractionForm(null)}>Cancel</Button><Button disabled={saving || !interactionForm.summary.trim() || !interactionForm.occurredAt} color="primary" variant="contained" onClick={async () => {
          setActionError(null);
          setSaving(true);
          try {
            const response = await fetch(`${hostname}/commercial/opportunities/${interactionForm.opportunityId}/interactions`, { method: "POST", headers, body: JSON.stringify({ ...interactionForm, opportunityId: undefined, company: undefined, occurredAt: localDateTimeInputToIso(interactionForm.occurredAt), nextActionAt: localDateTimeInputToIso(interactionForm.nextActionAt) }) });
            if (!response.ok) throw new Error(await response.text());
            setInteractionForm(null);
            setRevision(value => value + 1);
          } catch (cause) { setActionError({ action: "Client contact", message: commercialActionErrorMessage(cause) }); } finally { setSaving(false); }
        }}>Save contact</Button></DialogActions>
      </Dialog>}
      {conversion && (
        <Dialog open onClose={() => !saving && setConversion(null)} fullWidth maxWidth="md" aria-labelledby="customer-onboarding-title">
          <DialogTitle id="customer-onboarding-title">Onboard {conversion.opportunity.company}</DialogTitle>
          {!conversion.preview && !conversion.result && <DialogContent>
            {actionError && <Box className={classes.actionError} role="alert"><Typography variant="subtitle2" color="error">{actionError.action} could not be completed</Typography><Typography variant="body2">{actionError.message}</Typography></Box>}
            <Typography color="textSecondary">
              One governed workflow creates the customer and account, grants support access, creates client users, and handles their welcome messages.
            </Typography>
            <Card variant="outlined" style={{ marginTop: 16 }}><CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" gridGap={12}>
                <div><Typography variant="overline" color="textSecondary">SAVED ONBOARDING POLICY</Typography><Typography variant="subtitle1">{onboardingAction.label}</Typography></div>
                <Chip size="small" color={onboardingAction.allowed ? "primary" : "secondary"} label={conversion.workflowPolicy?.configuredMode || "OFF"} />
              </Box>
              <Typography variant="body2" color="textSecondary">{onboardingAction.detail}</Typography>
              {!onboardingAction.allowed && <Button size="small" color="primary" href="#/commercial-automation" style={{ marginTop: 8 }}>Open Automation controls</Button>}
            </CardContent></Card>
            <Grid container spacing={2} style={{ marginTop: 4 }}>
              <Grid item xs={12} md={7}>
                <Box display="grid" gridGap={16}>
                  <TextField variant="outlined" required label="Account name" value={conversion.accountName} onChange={(e) => setConversion({ ...conversion, accountName: e.target.value, supportEmail: supportEmailFromName(e.target.value) })} helperText="An existing account with this exact name will be reused." />
                  <TextField variant="outlined" required type="email" label="Support account" value={conversion.supportEmail} onChange={(e) => setConversion({ ...conversion, supportEmail: e.target.value })} helperText="Editable. Uses the shared password configured for existing SUPPORT accounts." />
                  <Typography variant="subtitle2">Primary client user</Typography>
                  <TextField variant="outlined" label="Contact name" value={conversion.contactName} onChange={(e) => setConversion({ ...conversion, contactName: e.target.value })} />
                  <TextField autoFocus={conversion.requiresCustomerEmail} variant="outlined" required error={!conversion.contactEmail.trim()} type="email" label="Customer email" value={conversion.contactEmail} onChange={(e) => setConversion({ ...conversion, contactEmail: e.target.value })} helperText={conversion.requiresCustomerEmail ? "This opportunity has no linked email. Add one to continue." : "A LICENSE user and customer contact will be created."} />
                  <TextField variant="outlined" multiline minRows={3} label="Additional client emails" value={conversion.extraEmails} onChange={(e) => setConversion({ ...conversion, extraEmails: e.target.value })} helperText="Optional. Add one email per line; each becomes a LICENSE user." />
                </Box>
              </Grid>
              <Grid item xs={12} md={5}>
                <Card variant="outlined"><CardContent>
                  <Typography variant="subtitle1">What will happen</Typography>
                  {["Create or reuse commercial customer","Create or reuse platform account","Grant ACCOUNT ADMIN to support","Create LICENSE users for client emails","Keep onboarding emails disabled during phase-one acceptance"].map((step, index) => <Box key={step} display="flex" mb={1}><Chip size="small" label={index + 1} style={{ marginRight: 8 }} /><Typography variant="body2">{step}</Typography></Box>)}
                  <Typography variant="caption" color="textSecondary">The operation is safe to retry. Email content is previewed for validation, but this phase does not send it.</Typography>
                </CardContent></Card>
              </Grid>
            </Grid>
          </DialogContent>}
          {!conversion.preview && !conversion.result && <DialogActions>
            <Button disabled={saving} onClick={() => setConversion(null)}>Cancel</Button>
            <Button disabled={saving || !conversion.accountName.trim() || !conversion.supportEmail.trim() || !conversion.contactEmail.trim()} color="primary" variant="contained" onClick={async () => {
              setActionError(null);
              setSaving(true);
              try {
                const extra = conversion.extraEmails.split(/[\n,;]/).map((email) => email.trim()).filter(Boolean);
                const response = await fetch(`${hostname}/commercial/opportunities/${conversion.opportunityId}/conversion-preview`, { method: "POST", headers, body: JSON.stringify({ accountName: conversion.accountName, supportEmail: conversion.supportEmail, licenseUsers: [{ name: conversion.contactName, email: conversion.contactEmail }, ...extra.map((email) => ({ name: email.split("@")[0], email }))], sendOnboardingEmails: false }) });
                if (!response.ok) throw new Error(await response.text());
                const preview = await response.json();
                setConversion({ ...conversion, preview, confirmations: {} });
              } catch (cause) { setActionError({ action: "Onboarding preview", message: commercialActionErrorMessage(cause) }); } finally { setSaving(false); }
            }}>{saving ? "Preparing preview…" : "Preview and review"}</Button>
          </DialogActions>}
          {conversion.preview && !conversion.result && <>
            <DialogContent>
              {actionError && <Box className={classes.actionError} role="alert"><Typography variant="subtitle2" color="error">{actionError.action} could not be completed</Typography><Typography variant="body2">{actionError.message}</Typography></Box>}
              <Typography variant="h6">Review the complete onboarding workflow</Typography>
              <Typography color="textSecondary">Nothing has been created yet. The saved policy below governs every listed step together; there is no separate hidden account, user or email action.</Typography>
              <Box mt={2} display="grid" gridGap={8}>
                {conversion.preview.steps.map((step, index) => <Box key={step.code} display="flex" alignItems="center"><Chip size="small" label={index + 1} style={{ marginRight: 8 }} /><Typography variant="body2">{step.label}</Typography></Box>)}
              </Box>
              <Card variant="outlined" style={{ marginTop: 16 }}><CardContent><Typography variant="overline" color="textSecondary">WHAT HAPPENS AFTER THIS REVIEW</Typography><Typography variant="subtitle1">{onboardingAction.label}</Typography><Typography variant="body2" color="textSecondary">{onboardingAction.detail}</Typography></CardContent></Card>
              <Card variant="outlined"><CardContent><Typography variant="subtitle1">Support access</Typography><Typography>{conversion.preview.supportEmail}</Typography><Typography variant="caption" color="textSecondary">{conversion.preview.supportCredentialPolicy}. The shared password is never exposed.</Typography></CardContent></Card>
              {conversion.preview.licenseUsers.map((user, index) => <Card variant="outlined" key={user.email} style={{ marginTop: 16 }}><CardContent>
                <Typography variant="subtitle1">Client user {index + 1}: {user.email}</Typography>
                <TextField fullWidth variant="outlined" label="Password handling" value={user.temporaryPassword} InputProps={{ readOnly: true }} helperText="The real random password is generated only when the workflow executes. The welcome message is preview-only in phase one and is not sent." style={{ marginTop: 8 }} />
                <Typography variant="subtitle2" style={{ marginTop: 16 }}>Rendered email preview</Typography>
                <Typography variant="body2"><strong>Subject:</strong> {user.emailPreview.subject}</Typography>
                <iframe title={`Onboarding email for ${user.email}`} sandbox="" srcDoc={user.emailPreview.html} style={{ width: "100%", minHeight: 260, border: "1px solid #ddd", marginTop: 8 }} />
              </CardContent></Card>)}
            </DialogContent>
            <DialogActions>
              <Button disabled={saving} onClick={() => setConversion({ ...conversion, preview: null, confirmations: {} })}>Back to edit</Button>
              <Button disabled={saving || !onboardingAction.allowed} color="primary" variant="contained" onClick={async () => {
                setActionError(null);
                setSaving(true);
                try {
                  const response = await fetch(`${hostname}/commercial/opportunities/${conversion.opportunityId}/convert-to-customer`, { method: "POST", headers, body: JSON.stringify({ requestId: conversion.requestId, accountName: conversion.preview.accountName, supportEmail: conversion.preview.supportEmail, licenseUsers: conversion.preview.licenseUsers.map(({ name, email }) => ({ name, email })), sendOnboardingEmails: false }) });
                  if (!response.ok) throw new Error(await response.text());
                  const result = await response.json();
                  setConversion({ ...conversion, result });
                  setRevision((value) => value + 1);
                } catch (cause) { setActionError({ action: "Customer onboarding", message: commercialActionErrorMessage(cause) }); } finally { setSaving(false); }
              }}>{saving ? "Requesting workflow…" : onboardingAction.label}</Button>
            </DialogActions>
          </>}
          {conversion.result && <WorkflowRunResult result={conversion.result} classes={classes} onClose={() => setConversion(null)} />}
        </Dialog>
      )}
    </Card>
  );
}
